// React
import { useState } from "react";
import { useTranslation } from "react-i18next";

// Redux
import { useDispatch } from "react-redux";
import { openSnackbar } from "Store/SnackbarSlice";

// Styles
import { styles } from "../styles";

// MUI
import {
  Button,
  CircularProgress,
  Dialog,
  Stack,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from "@mui/material";
import { InfoRow } from "Common/Components";

// Icons
import {
  AlternateEmail,
  CalendarMonth,
  ContactEmergency,
  Home,
  Language,
  Person,
  Phone,
} from "@mui/icons-material";

// Resources
import { updatePatient, createPatient } from "API/Resource/patient";
import {
  updateGuestPatient,
  createGuestPatient,
} from "API/Resource/guestPatient";
import {
  getAddressInfo,
  getHealthCardInfo,
  getLangLabel,
  getPhoneInfo,
  getStatusColour,
} from "./formatters";

const ConfirmEditModal = (props) => {
  const {
    open,
    onCloseModal,
    patientValues,
    mode = "edit",
    inSystem = true,
    hasUpdatedInfo = true,
    updateSuccessDescription,
    onContinueToBooking,
    onCreateSuccess,
    onUpdateSuccess,
    setOpenDuplicatePHINDialog,
  } = props;
  const [loading, setLoading] = useState(false);
  const dispatch = useDispatch();
  const { t } = useTranslation(); // Translation hook
  const isBookingMode = mode === "book";

  const notProvided = t("patientForm.notProvided");
  const dobValue = patientValues?.dob || notProvided;
  const addressInfo = getAddressInfo(patientValues, notProvided);
  const healthCardInfo = getHealthCardInfo(patientValues, t, notProvided);
  const phoneInfo = getPhoneInfo(patientValues, t, notProvided);

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const isExistingPatient = Boolean(patientValues?.id);

      // Update patient/guest
      if (isExistingPatient) {
        const { createdAt, updatedAt, ...editablePatientValues } =
          patientValues || {};
        const { userEmail, ...guestPatientValues } = editablePatientValues;
        const updatedPatientValues = inSystem
          ? editablePatientValues
          : guestPatientValues;
        const updatePatientRequest = inSystem
          ? updatePatient
          : updateGuestPatient;

        if (hasUpdatedInfo) {
          await updatePatientRequest(updatedPatientValues);
        }

        if (isBookingMode && onContinueToBooking) {
          await onContinueToBooking(updatedPatientValues);
          return;
        }

        dispatch(
          openSnackbar({
            message: t("confirmEditModal.patientUpdated"),
            description: updateSuccessDescription
              ? t(updateSuccessDescription)
              : undefined,
          })
        );
        onCloseModal();
        onUpdateSuccess?.(updatedPatientValues);
        return;
      }

      // Create patient/guest
      const createPatientRequest = inSystem
        ? createPatient
        : createGuestPatient;

      const createdPatient = await createPatientRequest(patientValues);

      if (!inSystem) {
        const notificationFailed =
          createdPatient?.notification?.attempted &&
          createdPatient?.notification?.sent === false;
        const notificationMessage = notificationFailed
          ? createdPatient?.notification?.error ||
            t("mbsPatientRegistration.createSuccessNotificationWarning")
          : t("mbsPatientRegistration.createSuccess");

        dispatch(
          openSnackbar({
            message: notificationMessage,
            severity: notificationFailed ? "warning" : "success",
          })
        );

        if (isBookingMode && onContinueToBooking) {
          await onContinueToBooking(createdPatient);
          onCloseModal();
          return;
        }
      }

      onCloseModal();
      onCreateSuccess?.(createdPatient);
    } catch (error) {
      try {
        const isDuplicateHealthCardNumber =
          error?.message === "DUPLICATE_ACTIVE_HEALTH_CARD_NUMBER";

        if (isDuplicateHealthCardNumber && setOpenDuplicatePHINDialog) {
          setOpenDuplicatePHINDialog(true);
          return;
        }

        const errorSnackbar = isDuplicateHealthCardNumber
          ? "confirmEditModal.errors.duplicateHealthCardNumber"
          : "confirmEditModal.errors.default";

        dispatch(
          openSnackbar({
            message: t(`${errorSnackbar}.message`),
            description: t(`${errorSnackbar}.description`),
            severity: "error",
          })
        );
      } finally {
        onCloseModal();
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onCloseModal} fullWidth maxWidth="sm">
      <DialogTitle sx={styles.headingModal}>
        {t("confirmEditModal.title")}
      </DialogTitle>
      <DialogContent sx={styles.confirmContent}>
        <DialogContentText>
          {t("confirmEditModal.verifyDetails")}
        </DialogContentText>
        <InfoRow
          IconComponent={Person}
          text={`${patientValues.firstName} ${patientValues.lastName}`}
        />
        <InfoRow
          IconComponent={CalendarMonth}
          statusColour={getStatusColour(patientValues.dob)}
          text={`${t("patientForm.dobLabel")}: ${dobValue}`}
        />
        <InfoRow
          IconComponent={Home}
          statusColour={addressInfo.colour}
          text={`${t("patientForm.addressLabel")}: ${addressInfo.text}`}
        />
        <InfoRow
          IconComponent={ContactEmergency}
          statusColour={healthCardInfo.colour}
          text={healthCardInfo.text}
        />
        <InfoRow
          IconComponent={Language}
          statusColour={getStatusColour(patientValues.preferredLanguage)}
          text={`${t("patientForm.preferredLanguageLabel")}: ${getLangLabel(patientValues.preferredLanguage, t) || notProvided}`}
        />
        <InfoRow
          IconComponent={AlternateEmail}
          statusColour={getStatusColour(patientValues.email)}
          text={`${t("patientForm.emailLabel")}: ${patientValues.email || notProvided}`}
        />
        <InfoRow
          IconComponent={Phone}
          statusColour={getStatusColour(patientValues.primaryPhone)}
          text={phoneInfo.text}
        />
      </DialogContent>
      <Stack
        direction={{ xs: "column-reverse", sm: "row" }}
        spacing={2}
        sx={styles.buttonStack}
      >
        <Button
          size="large"
          variant="text"
          onClick={onCloseModal}
          sx={styles.textButton}
          disabled={loading}
        >
          {t("buttons.cancel")}
        </Button>

        <Button
          size="large"
          variant="contained"
          onClick={handleSubmit}
          sx={styles.mainButton(isBookingMode)}
          disabled={loading}
          startIcon={
            loading ? <CircularProgress size={18} color="inherit" /> : null
          }
        >
          {isBookingMode
            ? t("buttons.continueToBooking")
            : t("buttons.confirm")}
        </Button>
      </Stack>
    </Dialog>
  );
};

export default ConfirmEditModal;
